import * as React from "react";

import { ToastActionEnum } from "@/enums";
import type { ToastActionElement, ToastProps } from "@/ui/toast";

const TOAST_LIMIT = 1;
const TOAST_REMOVE_DELAY = 1000000;

type ToasterToast = ToastProps & {
    action?: ToastActionElement,
    description?: React.ReactNode,
    id: string,
    title?: React.ReactNode
}

let count = 0;

const genId = () => {
    count = (count + 1) % Number.MAX_SAFE_INTEGER;
    return count.toString();
};

type Action = | {
    toast: ToasterToast,
    type: ToastActionEnum.ADD_TOAST
} | {
    toast: Partial<ToasterToast>,
    type: ToastActionEnum.UPDATE_TOAST
} | {
    toastId?: ToasterToast["id"],
    type: ToastActionEnum.DISMISS_TOAST
} | {
    toastId?: ToasterToast["id"],
    type: ToastActionEnum.REMOVE_TOAST
}

interface State { toasts: ToasterToast[] }

const toastTimeouts = new Map<string, ReturnType<typeof setTimeout>>();

const addToRemoveQueue = (toastId: string) => {
    if (toastTimeouts.has(toastId)) return;

    const timeout = setTimeout(
        () => {
            toastTimeouts["delete"](toastId);

            dispatch({ // eslint-disable-line
                toastId,
                type: ToastActionEnum.REMOVE_TOAST,
            });
        },
        TOAST_REMOVE_DELAY,
    );

    toastTimeouts.set(
        toastId,
        timeout,
    );
};

export const reducer = (state: State, action: Action): State => {
    switch (action.type) {
    case ToastActionEnum.ADD_TOAST:
        return {
            ...state,
            toasts: [action.toast, ...state.toasts].slice(
                0,
                TOAST_LIMIT,
            ),
        };

    case ToastActionEnum.UPDATE_TOAST:
        return {
            ...state,
            toasts: state.toasts.map((t) => (t.id === action.toast.id ? {
                ...t,
                ...action.toast,
            } : t)),
        };

    case ToastActionEnum.DISMISS_TOAST: {
        const { toastId } = action;

        /* ! Side effects ! - This could be extracted into a dismissToast() action, but I'll keep it here for simplicity */
        if (toastId) addToRemoveQueue(toastId);

        else {
            state.toasts.forEach((toast) => {
                addToRemoveQueue(toast.id);
            });
        }

        return {
            ...state,
            toasts: state.toasts.map((t) => (t.id === toastId || toastId === undefined ? {
                ...t,
                open: false,
            } : t)),
        };
    }
    case ToastActionEnum.REMOVE_TOAST:
        if (action.toastId === undefined) {
            return {
                ...state,
                toasts: [],
            };
        }

        return {
            ...state,
            toasts: state.toasts.filter((t) => t.id !== action.toastId),
        };
    }
};

const listeners: Array<(state: State) => void> = []; // eslint-disable-line

let memoryState: State = { toasts: [] };

const dispatch = (action: Action) => {
    memoryState = reducer(
        memoryState,
        action,
    );

    listeners.forEach((listener) => {
        listener(memoryState);
    });
};

type Toast = Omit<ToasterToast, "id">

const toast = ({ ...props }: Toast) => {
    const id = genId();

    const update = (props: ToasterToast) => dispatch({ // eslint-disable-line
        toast: {
            ...props,
            id,
        },
        type: ToastActionEnum.UPDATE_TOAST,
    });

    const dismiss = () => dispatch({
        toastId: id,
        type: ToastActionEnum.DISMISS_TOAST,
    });

    dispatch({
        toast: {
            ...props,
            id,
            onOpenChange: (open) => {
                if (!open) dismiss();
            },
            open: true,
        },
        type: ToastActionEnum.ADD_TOAST,
    });

    return {
        dismiss,
        id,
        update,
    };
};

const useToast = () => {
    const [state, setState] = React.useState<State>(memoryState);

    React.useEffect(
        () => {
            listeners.push(setState);

            return () => {
                const index = listeners.indexOf(setState);
                if (index > -1) {
                    listeners.splice(
                        index,
                        1,
                    );
                }
            };
        },
        [state],
    );

    return {
        ...state,
        dismiss: (toastId?: string) => dispatch({
            toastId,
            type: ToastActionEnum.DISMISS_TOAST,
        }),
        toast,
    };
};

export { toast, useToast };
